import { useMemo, useState } from 'react'
import { ArrowLeft, ArrowRight, Save, Check } from 'lucide-react'
import { VisitStepper } from './VisitStepper'
import { Button } from '@/components/common/Button'
import { VISIT_STEPS } from '@/lib/constants'

// Step components
import { ChiefComplaintStep } from './steps/ChiefComplaintStep'
import { HistoryPresentIllnessStep } from './steps/HistoryPresentIllnessStep'
import { MedicalHistoryStep } from './steps/MedicalHistoryStep'
import { PersonalHistoryStep } from './steps/PersonalHistoryStep'
import { DentalHistoryStep } from './steps/DentalHistoryStep'
import { VitalsStep } from './steps/VitalsStep'
import { ExtraOralStep } from './steps/ExtraOralStep'
import { IntraOralStep } from './steps/IntraOralStep'
import { InvestigationStep } from './steps/InvestigationStep'
import { ProvisionalDiagnosisStep } from './steps/ProvisionalDiagnosisStep'
import { TreatmentPlanStep } from './steps/TreatmentPlanStep'
import { PrescriptionStep } from './steps/PrescriptionStep'
import { ConsentStep } from './steps/ConsentStep'
import { DoctorNotesStep } from './steps/DoctorNotesStep'

// Validators
import {
  chiefComplaintSchema,
  historyPresentIllnessSchema,
  medicalHistorySchema,
  personalHistorySchema,
  dentalHistorySchema,
  vitalsSchema,
  extraOralSchema,
  intraOralSchema,
  investigationSchema,
  provisionalDiagnosisSchema,
  treatmentPlanSchema,
  prescriptionSchema,
  consentSchema,
  doctorNotesSchema,
} from '@/lib/validators'
import { hasMeaningfulData } from '@/lib/utils'

const STEP_COMPONENT = {
  chief_complaint: ChiefComplaintStep,
  history_present_illness: HistoryPresentIllnessStep,
  medical_history: MedicalHistoryStep,
  personal_history: PersonalHistoryStep,
  dental_history: DentalHistoryStep,
  vitals: VitalsStep,
  extra_oral_examination: ExtraOralStep,
  intra_oral_examination: IntraOralStep,
  investigation: InvestigationStep,
  provisional_diagnosis: ProvisionalDiagnosisStep,
  treatment_plan: TreatmentPlanStep,
  prescription: PrescriptionStep,
  consent: ConsentStep,
  doctor_notes: DoctorNotesStep,
}

const STEP_SCHEMA = {
  chief_complaint: chiefComplaintSchema,
  history_present_illness: historyPresentIllnessSchema,
  medical_history: medicalHistorySchema,
  personal_history: personalHistorySchema,
  dental_history: dentalHistorySchema,
  vitals: vitalsSchema,
  extra_oral_examination: extraOralSchema,
  intra_oral_examination: intraOralSchema,
  investigation: investigationSchema,
  provisional_diagnosis: provisionalDiagnosisSchema,
  treatment_plan: treatmentPlanSchema,
  prescription: prescriptionSchema,
  consent: consentSchema,
  doctor_notes: doctorNotesSchema,
}

/**
 * `prefixStep` lets the parent (AddPatient) slot a custom first step (PatientDetailsStep)
 * before the 14 visit sections. The flow keeps that step internally as index 0 if provided.
 */
export function VisitFormFlow({
  data,                       // { [sectionKey]: sectionData }  ← from draft store
  onChange,                   // (sectionKey, value) => void
  stepIndex,
  onStepChange,
  onSubmit,                   // (cleanedData) => Promise<void>
  submitting = false,
  prefixStep = null,          // { key, label, component, schema?, transform? }
  submitLabel = 'Save visit',
}) {
  const [errors, setErrors] = useState({})

  const allSteps = useMemo(() => {
    return prefixStep ? [{ key: prefixStep.key, label: prefixStep.label }, ...VISIT_STEPS] : VISIT_STEPS
  }, [prefixStep])

  const total = allSteps.length
  const isLast = stepIndex === total - 1
  const isFirst = stepIndex === 0
  const currentKey = allSteps[stepIndex]?.key

  const StepComponent =
    prefixStep && stepIndex === 0 ? prefixStep.component : STEP_COMPONENT[currentKey]
  const stepSchema =
    prefixStep && stepIndex === 0 ? prefixStep.schema : STEP_SCHEMA[currentKey]

  const stepValue = data[currentKey]

  const completedSet = useMemo(() => {
    const set = new Set()
    allSteps.forEach((s, i) => {
      if (i === stepIndex) return
      if (hasMeaningfulData(data[s.key])) set.add(i)
    })
    return set
  }, [allSteps, data, stepIndex])

  const validateCurrent = () => {
    if (!stepSchema) return { ok: true }
    const parse = stepSchema.safeParse(stepValue || {})
    if (parse.success) return { ok: true }
    const errs = {}
    parse.error.issues.forEach((iss) => {
      errs[iss.path[0]] = iss.message
    })
    return { ok: false, errs }
  }

  const goNext = () => {
    const result = validateCurrent()
    if (!result.ok) {
      setErrors(result.errs)
      return
    }
    setErrors({})
    onStepChange?.(Math.min(stepIndex + 1, total - 1))
  }

  const goPrev = () => {
    setErrors({})
    onStepChange?.(Math.max(stepIndex - 1, 0))
  }

  const jumpTo = (i) => {
    setErrors({})
    onStepChange?.(i)
  }

  const submit = async () => {
    const result = validateCurrent()
    if (!result.ok) {
      setErrors(result.errs)
      return
    }
    setErrors({})
    await onSubmit?.()
  }

  return (
    <div className="space-y-6">
      <VisitStepper
        activeIndex={stepIndex}
        onJumpTo={jumpTo}
        completedSet={completedSet}
        steps={allSteps}
      />

      <div className="rounded-2xl bg-white p-5 shadow-soft ring-1 ring-ink-100 sm:p-7">
        {StepComponent ? (
          <StepComponent
            value={stepValue}
            onChange={(val) => onChange?.(currentKey, val)}
            errors={errors}
          />
        ) : null}
      </div>

      {/* Sticky footer with prev / next / submit */}
      <div className="sticky bottom-0 -mx-4 sm:-mx-6 lg:-mx-8 z-10 border-t border-ink-100 bg-cream/95 px-4 py-3 backdrop-blur-md sm:px-6 lg:px-8">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Button
            variant="outline"
            onClick={goPrev}
            disabled={isFirst || submitting}
            leftIcon={<ArrowLeft className="h-4 w-4" />}
          >
            Previous
          </Button>

          <div className="flex items-center justify-between gap-2 sm:justify-end">
            <p className="text-xs font-semibold text-ink-500">
              Step {stepIndex + 1} of {total}
            </p>
            {isLast ? (
              <Button
                onClick={submit}
                loading={submitting}
                leftIcon={!submitting ? <Save className="h-4 w-4" /> : null}
                variant="accent"
              >
                {submitLabel}
              </Button>
            ) : (
              <Button
                onClick={goNext}
                rightIcon={<ArrowRight className="h-4 w-4" />}
              >
                Next
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
